<script lang="ts">
  /**
   * Signals — what the house says in the morning, and what it is allowed to say.
   * Items 22, 23 and 24.
   *
   *   Digest preview  item 23 — compose without waiting for 06:20, mute a module
   *   Flags           item 22 — per-feature switches, each with a removal date
   *   Models          item 24 — AI spend per module, with caps
   *
   * The digest panel exists because the only way to see the morning message used
   * to be to wait for the morning. A message nobody can inspect is a message that
   * quietly rots.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { stepUp } from "../lib/stepup.svelte";

  type Line = { source: string; text: string; deepLink: string; severity: string; digestCount?: number; escalate?: boolean };
  type Digest = { day: string; lines: Line[]; muted: string[]; text: string; chars: number; budget: number; overBudget: boolean; quietHours: boolean };
  type Flag = {
    portalId: string;
    flagId: string;
    enabled: boolean;
    enabledForAnyone: boolean;
    description: string;
    rolloutRoles: string[];
    updatedAt: string | null;
    updatedBy: string | null;
  };
  type Use = {
    moduleId: string;
    useId: string;
    modelId: string;
    purpose: string;
    provider: string;
    callsToday: number;
    spentTodayZAR: number;
    spentMonthZAR: number;
    capPerDayZAR: number | null;
    nearCap: boolean;
  };

  type Panel<T> = { data: T | null; error: string | null; loading: boolean };

  const digest = $state<Panel<Digest>>({ data: null, error: null, loading: true });
  const mutes = $state<Panel<{ mutes: Record<string, string> }>>({ data: null, error: null, loading: true });
  const flags = $state<Panel<{ flags: Flag[] }>>({ data: null, error: null, loading: true });
  const models = $state<Panel<{ uses: Use[]; totals: { todayZAR: number; monthZAR: number }; note: string | null }>>({
    data: null, error: null, loading: true,
  });

  let busy = $state<string | null>(null);
  let toast = $state<{ kind: "ok" | "warn"; text: string } | null>(null);

  async function fill<T>(p: Panel<T>, path: string) {
    p.loading = true;
    try {
      p.data = await api.get<T>(path);
      p.error = null;
    } catch (e) {
      p.error = e instanceof ApiError ? e.message : "Unavailable";
    } finally {
      p.loading = false;
    }
  }

  function loadAll() {
    void fill(digest, "/digest/preview");
    void fill(mutes, "/digest/mutes");
    void fill(flags, "/flags");
    void fill(models, "/models");
  }

  $effect(() => loadAll());

  /** Sources worth offering a mute for: whatever spoke, plus fabric itself. */
  const sources = $derived([
    ...new Set([...(digest.data?.lines ?? []).map((l) => l.source), ...(digest.data?.muted ?? []), "fabric"]),
  ].sort());

  async function mute(source: string, days: number) {
    busy = source;
    try {
      await api.patch("/digest/mute", { source, days });
      toast = { kind: "ok", text: days === 0 ? `${source} unmuted.` : `${source} muted for ${days} days.` };
      await Promise.all([fill(digest, "/digest/preview"), fill(mutes, "/digest/mutes")]);
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not change the mute" };
    } finally {
      busy = null;
    }
  }

  async function sendTest() {
    busy = "test";
    try {
      const res = await api.post<{ note: string }>("/digest/test");
      toast = { kind: "ok", text: res.note };
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not send a test" };
    } finally {
      busy = null;
    }
  }

  async function toggleFlag(f: Flag) {
    busy = `${f.portalId}/${f.flagId}`;
    try {
      const ok = await stepUp.guard(
        () =>
          api.patch(`/flags/${f.portalId}/${f.flagId}`, {
            enabled: !f.enabledForAnyone,
            description: f.description,
            rolloutRoles: f.rolloutRoles,
          }),
        `${f.enabledForAnyone ? "Disabling" : "Enabling"} ${f.flagId}`,
      );
      toast = ok
        ? { kind: "ok", text: `${f.flagId} ${f.enabledForAnyone ? "disabled" : "enabled"}.` }
        : { kind: "warn", text: "Cancelled." };
      if (ok) await fill(flags, "/flags");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not flip that flag" };
    } finally {
      busy = null;
    }
  }
</script>

<div class="wrap">
  {#if toast}
    <div class="attn" class:attn--warn={toast.kind === "warn"} role="status">
      <span class="attn__badge" aria-hidden="true"><Icon name={toast.kind === "warn" ? "alert" : "check"} size={12} /></span>
      <span class="attn__text">{toast.text}</span>
    </div>
  {/if}

  <!-- ── Digest ────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Tomorrow's digest, as it stands</span>
      <span class="acts">
        <button class="lnk" onclick={() => fill(digest, "/digest/preview")}>Refresh</button>
        <button class="btn-primary" disabled={busy === "test"} onclick={sendTest}>
          {busy === "test" ? "Sending…" : "Send test"}
        </button>
      </span>
    </div>

    {#if digest.loading}
      <p class="sub pad">Composing…</p>
    {:else if digest.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {digest.error}</p>
    {:else if digest.data}
      {#if digest.data.lines.length === 0}
        <p class="sub pad">
          <Icon name="check" size={12} /> Nothing to say. A module contributes a line only when it has one — that
          restraint is the whole contract.
        </p>
      {:else}
        {#each digest.data.lines as l (l.source + l.text)}
          <div class="line" class:line--warn={l.severity !== "info"}>
            <span class="line__src num">{l.source}</span>
            <span class="line__t">{l.text}</span>
            {#if l.escalate}
              <span class="status status--warn"><Icon name="alert" size={12} /> ignored {l.digestCount}×</span>
            {/if}
          </div>
        {/each}
        <p class="sub foot">
          <Icon name={digest.data.overBudget ? "alert" : "check"} size={12} />
          {digest.data.lines.length} line{digest.data.lines.length === 1 ? "" : "s"} ·
          <span class="num">{digest.data.chars}</span> of {digest.data.budget} characters
          {#if digest.data.overBudget}— longer than this stops being read aloud usefully{/if}
        </p>
      {/if}

      {#if digest.data.muted.length > 0}
        <!-- Shown as muted rather than simply absent: a missing line should never
             be mysterious. -->
        <p class="sub foot"><Icon name="pause" size={12} /> Muted, so held back: {digest.data.muted.join(", ")}.</p>
      {/if}

      <div class="mutes">
        {#each sources as s (s)}
          {@const until = mutes.data?.mutes?.[s]}
          <div class="mute">
            <span class="mute__n">{s}</span>
            {#if until}
              <span class="sub">muted to {dateZA(until)}</span>
              <button class="lnk" disabled={busy === s} onclick={() => mute(s, 0)}>Unmute</button>
            {:else}
              <button class="lnk" disabled={busy === s} onclick={() => mute(s, 1)}>1d</button>
              <button class="lnk" disabled={busy === s} onclick={() => mute(s, 7)}>7d</button>
              <button class="lnk" disabled={busy === s} onclick={() => mute(s, 30)}>30d</button>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
  </section>

  <!-- ── Flags ─────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec"><span class="kicker">Feature flags</span><span class="sub">each one carries a removal date</span></div>
    {#if flags.loading}
      <p class="sub pad">Loading…</p>
    {:else if flags.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {flags.error}</p>
    {:else if (flags.data?.flags ?? []).length === 0}
      <p class="sub pad">
        <Icon name="pause" size={12} /> No flags. Modules create them; the first will be Vault's OCR pipeline.
      </p>
    {:else}
      <div class="scroll">
        <table>
          <thead><tr><th>Flag</th><th>Portal</th><th>Rollout</th><th>State</th><th></th></tr></thead>
          <tbody>
            {#each flags.data!.flags as f (f.portalId + f.flagId)}
              <tr>
                <td>
                  <div class="nm">{f.flagId}</div>
                  <div class="sub">{f.description}</div>
                </td>
                <td class="sub num">{f.portalId}</td>
                <td class="sub">{f.rolloutRoles.length === 0 ? "everyone" : f.rolloutRoles.join(", ")}</td>
                <td>
                  {#if f.enabledForAnyone}
                    <span class="status status--ok"><Icon name="check" size={12} /> on</span>
                  {:else}
                    <span class="status status--idle"><Icon name="pause" size={12} /> off</span>
                  {/if}
                </td>
                <td class="acts">
                  <button class="lnk" disabled={busy === `${f.portalId}/${f.flagId}`} onclick={() => toggleFlag(f)}>
                    {f.enabledForAnyone ? "Turn off" : "Turn on"}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </section>

  <!-- ── Models ────────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">AI spend</span>
      <span class="sub">
        {#if models.data}today R{models.data.totals.todayZAR.toFixed(2)} · month R{models.data.totals.monthZAR.toFixed(2)}{/if}
      </span>
    </div>
    {#if models.loading}
      <p class="sub pad">Loading…</p>
    {:else if models.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {models.error}</p>
    {:else if models.data?.note}
      <p class="sub pad"><Icon name="pause" size={12} /> {models.data.note}</p>
    {:else if models.data}
      <div class="scroll">
        <table>
          <thead><tr><th>Module</th><th>Model</th><th>Purpose</th><th>Calls today</th><th>Today</th><th>Month</th><th>Cap</th></tr></thead>
          <tbody>
            {#each models.data.uses as u (u.moduleId + u.useId)}
              <tr>
                <td class="nm">{u.moduleId}</td>
                <td class="sub num">{u.modelId}</td>
                <td class="sub">{u.purpose}</td>
                <td class="num">{u.callsToday}</td>
                <td class="num">R{u.spentTodayZAR.toFixed(2)}</td>
                <td class="num">R{u.spentMonthZAR.toFixed(2)}</td>
                <td>
                  {#if u.capPerDayZAR == null}
                    <span class="sub">none</span>
                  {:else if u.nearCap}
                    <span class="status status--warn"><Icon name="alert" size={12} /> R{u.capPerDayZAR.toFixed(2)}</span>
                  {:else}
                    <span class="sub num">R{u.capPerDayZAR.toFixed(2)}</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </section>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card { padding: 13px 15px 15px; }
  .sec { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding-bottom: 9px; }
  .acts { display: flex; align-items: center; gap: 10px; }
  .pad { padding: 6px 0 2px; display: flex; align-items: center; gap: 6px; }
  .foot { margin: 9px 0 0; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.7812rem; }
  th { text-align: left; font-size: 0.72rem; font-weight: 700; color: var(--mut); padding: 6px 8px;
       border-bottom: 1px solid var(--line); white-space: nowrap; }
  td { padding: 9px 8px; border-bottom: 1px solid var(--line); vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  .nm { font-weight: 700; color: var(--tx); }
  .lnk { font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  button:disabled { opacity: 0.45; cursor: default; }

  .line { display: flex; align-items: baseline; gap: 9px; padding: 8px 10px; margin-bottom: 5px;
          border-radius: var(--r-control); box-shadow: inset 0 0 0 1px var(--line); border-left: 2px solid var(--ok); }
  .line--warn { border-left-color: var(--warn); }
  .line__src { font-size: 0.72rem; font-weight: 800; color: var(--mut); min-width: 62px; }
  .line__t { flex: 1; font-size: 0.7812rem; color: var(--tx); }

  .mutes { display: flex; flex-direction: column; gap: 4px; margin-top: 12px;
           border-top: 1px solid var(--line); padding-top: 10px; }
  .mute { display: flex; align-items: center; gap: 10px; }
  .mute__n { flex: 1; font-size: 0.75rem; font-weight: 700; color: var(--tx2); }
</style>
